import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { ImageManager } from "@/components/ImageManager";

const EMPTY = {
  title: "", description: "", brand: "", category_id: "",
  condition: "Nuovo", year: "", price: "", sale_price: "",
  quantity: 1, sku: "", images: [], status: "draft",
  featured: false, is_new_arrival: true,
  free_shipping: false, bulky: false,
};

export default function ProductForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const c = await api.get("/categories", { params: { include_hidden: true } });
      setCats(c.data);
      if (isEdit) {
        const { data } = await api.get(`/products/${id}`);
        setForm({
          ...EMPTY,
          ...data,
          year: data.year ?? "",
          sale_price: data.sale_price ?? "",
          sku: data.sku ?? "",
          category_id: data.category_id ?? "",
        });
      }
    })();
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (status) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year === "" ? null : parseInt(form.year),
        price: parseFloat(form.price),
        sale_price: form.sale_price === "" || form.sale_price === null ? null : parseFloat(form.sale_price),
        quantity: parseInt(form.quantity),
        sku: form.sku || null,
        category_id: form.category_id || null,
        status,
      };
      if (!payload.title || isNaN(payload.price)) {
        toast.error("Titolo e prezzo obbligatori");
        setSaving(false);
        return;
      }
      if (isEdit) {
        await api.put(`/admin/products/${id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success(status === "published" ? "Prodotto pubblicato" : "Bozza salvata");
      nav("/admin/prodotti");
    } catch (e) {
      toast.error("Errore salvataggio", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title={isEdit ? "Modifica prodotto" : "Nuovo prodotto"} back="/admin/prodotti">
      <div className="bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-5">
        {/* Images */}
        <div>
          <Label>Fotografie</Label>
          <div className="mt-2">
            <ImageManager images={form.images} onChange={(imgs) => set("images", imgs)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="title">Titolo *</Label>
            <Input id="title" data-testid="form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="description">Descrizione</Label>
            <Textarea id="description" data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="brand">Marca</Label>
            <Input id="brand" data-testid="form-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Categoria</Label>
            <Select value={form.category_id || ""} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger data-testid="form-category" className="mt-1 rounded-xl h-10">
                <SelectValue placeholder="Seleziona categoria" />
              </SelectTrigger>
              <SelectContent>
                {cats.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Condizione</Label>
            <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger data-testid="form-condition" className="mt-1 rounded-xl h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nuovo">Nuovo</SelectItem>
                <SelectItem value="Usato">Usato</SelectItem>
                <SelectItem value="Vintage">Vintage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="year">Anno (opzionale)</Label>
            <Input id="year" data-testid="form-year" type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="price">Prezzo € *</Label>
            <Input id="price" data-testid="form-price" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="sale">Prezzo offerta € (opzionale)</Label>
            <Input id="sale" data-testid="form-sale" type="number" step="0.01" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="qty">Quantità</Label>
            <Input id="qty" data-testid="form-qty" type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="sku">SKU (opzionale)</Label>
            <Input id="sku" data-testid="form-sku" value={form.sku} onChange={(e) => set("sku", e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="form-featured" checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
            <Label>In evidenza</Label>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="form-new" checked={form.is_new_arrival} onCheckedChange={(v) => set("is_new_arrival", v)} />
            <Label>Nuovo arrivo</Label>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="form-free-shipping" checked={form.free_shipping} onCheckedChange={(v) => set("free_shipping", v)} />
            <Label>Spedizione gratuita</Label>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch data-testid="form-bulky" checked={form.bulky} onCheckedChange={(v) => set("bulky", v)} />
            <Label>Voluminoso</Label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button data-testid="save-draft-btn" onClick={() => submit("draft")} disabled={saving} variant="outline" className="rounded-full h-12">
            Salva come bozza
          </Button>
          <Button data-testid="publish-btn" onClick={() => submit("published")} disabled={saving} className="rounded-full h-12 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold">
            {isEdit ? "Salva e pubblica" : "Pubblica"}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
